import React, { useEffect, useState } from 'react';
import { ConnectionBanner } from '@/features/shared/components/recovery/ConnectionBanner';
import { reconcile as reconcileDesktopNotifications } from '@/features/shared/components/notifications/desktopNotifications';
import { Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { PageBoundary } from '@/features/shared/components/PageBoundary';
import { TenantAwareNavigationSidebar } from './TenantAwareNavigationSidebar';
import { DelegatedRoute } from '@/features/delegation/DelegatedRoute';
import { PlatformBanner } from '@/features/shared/components/PlatformBanner';
import { CommandPalette } from '@/features/shared/components/command/CommandPalette';
import { Header } from './Header';
import { MessagesPanelProvider } from '@/features/conversations/MessagesPanel';
import { SidebarProvider } from '@/features/shared/components/ui/sidebar';
import { AssistantPanel } from '@/features/shared/components/AssistantPanel';
import { PersistentPlayer } from '@/features/music/player/PersistentPlayer';
import { useResolvedNavPages } from '@/features/navigation/hooks/useResolvedNavPages';
import { Button } from '@/features/shared/components/ui/button';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { getCurrentTenant } from '@/lib/hrApi';
import { setCurrentTenant } from '@/features/auth/state/tenant-state/tenantSlice';
import { shouldHealTenantPath, swapTenantSegment } from '@/features/auth/utils/tenantPath';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { permissionsForRoles } from '@/rbac/rbac.generated';
import { RequireAnyPermissionRoute } from '@/rbac/RequireAnyPermissionRoute';
import { updateUser } from '@/features/auth/state/auth-state/authSlice';
import { adminPortalConfig } from '@/config/app.config';
import type { Permission, FeatureFlag, UserRole } from '@/features/navigation/types';
import { useFeatureFlags as usePlatformFeatureFlags } from '@/features/shared/featureFlags';

// Import pages
import { EmployeeDashboard } from '@/features/pages/employee/EmployeeDashboard';
import { MyCalendarPage } from '@/features/pages/employee/MyCalendarPage';
import { ReferEarnPage } from '@/features/pages/employee/ReferEarnPage';
import { Page as PersonalPage } from '@/features/pages/personal';
import { Page as TimeOffPage } from '@/features/pages/time-off';
import { ConversationsPage } from '@/features/conversations/pages/ConversationsPage';
import { EmployeesPage } from '@/features/pages/employees';
import { EmployeeDetailPage } from '@/features/pages/employees/EmployeeDetailPage';
import { EmployeeDocuments } from '@/features/pages/employee/EmployeeDocuments';
import { HelpPage } from '@/features/pages/help/HelpPage';
import { NotificationsPage } from '@/features/pages/notifications/NotificationsPage';
import { PaymentProviderPage } from '@/features/payments/pages/PaymentProviderPage';
import { PayoutsPage } from '@/features/payments/pages/PayoutsPage';
import { CommissionPage } from '@/features/commission/pages/CommissionPage';
import { SettingsPage } from '@/features/pages/settings/SettingsPage';
import { AccountSettingsPage } from '@/features/pages/settings/AccountSettingsPage';
import { FeatureManagementPage } from '@/features/pages/settings/FeatureManagementPage';
import { EmailIntegrationsPage } from '@/features/pages/settings/EmailIntegrationsPage';
import { AutomationSettingsPage } from '@/features/pages/settings/AutomationSettingsPage';
import { TrainingPage } from '@/features/pages/employee/TrainingPage';
import { LearningAdminPage } from '@/features/pages/admin/LearningAdminPage';
import { TeamLearningPage } from '@/features/pages/manager/TeamLearningPage';
import { PerformancePage } from '@/features/pages/performance/PerformancePage';
import { MusicPage } from '@/features/music/MusicPage';
import { WebPage } from '@/features/web/WebPage';
import { WebSettingsPage } from '@/features/web/admin/WebSettingsPage';
import { WebPrivacyPage } from '@/features/web/WebPrivacyPage';
import { EverydayServicesPage } from '@/features/everyday-services/pages/EverydayServicesPage';
import { HomeToday } from '@/features/home/HomeToday';
import { HRActionCenter } from '@/features/pages/hr/HRActionCenter';
import { HRActionSettings } from '@/features/pages/hr/HRActionSettings';
import { SurveysAdmin } from '@/features/pages/hr/SurveysAdmin';
import { MySurveys } from '@/features/pages/employee/MySurveys';
import { LeaveApprovalsPage } from '@/features/pages/manager/LeaveApprovalsPage';
import { TeamManagementPage } from '@/features/pages/manager/TeamManagementPage';
import { TeamCalendarPage } from '@/features/pages/manager/TeamCalendarPage';
import { ManagerReportsPage } from '@/features/pages/manager/ManagerReportsPage';
import { ReportsPage } from '@/features/pages/reports/ReportsPage';
import { ReportViewerPage } from '@/features/reports/ReportViewerPage';
import { ReportBuilderPage } from '@/features/reports/ReportBuilderPage';
import { AdministrationPage } from '@/features/pages/admin/AdministrationPage';
import { CompliancePage } from '@/features/pages/admin/CompliancePage';
import { AuditCenterPage } from '@/features/pages/admin/AuditCenterPage';
import { BillingPage } from '@/features/pages/admin/BillingPage';
import { ConsentCenterPage } from '@/features/pages/ConsentCenterPage';
import { UsersRolesPage } from '@/features/pages/admin/UsersRolesPage';
import { RolesPermissionsPage } from '@/features/pages/admin/RolesPermissionsPage';
import ScopePolicyPage from '@/features/pages/admin/ScopePolicyPage';
import DelegationsPage from '@/features/pages/admin/DelegationsPage';
import DelegationPolicyPage from '@/features/pages/admin/DelegationPolicyPage';
import { DepartmentsPage } from '@/features/pages/admin/DepartmentsPage';
import { LeavePoliciesPage } from '@/features/pages/admin/LeavePoliciesPage';
import { HolidaysPage } from '@/features/pages/admin/HolidaysPage';
import { BulkImportPage } from '@/features/pages/admin/BulkImportPage';
import { MigrationCenterPage } from '@/features/pages/migration/MigrationCenterPage';
import { BulkSalariesPage } from '@/features/pages/admin/BulkSalariesPage';
import { JobTitlesPage } from '@/features/pages/admin/JobTitlesPage';
import { AiAssistantPage } from '@/features/pages/admin/AiAssistantPage';
import { ChangeApprovalsPage } from '@/features/pages/admin/ChangeApprovalsPage';
import { PaymentMethodApprovalsPage } from '@/features/pages/admin/PaymentMethodApprovalsPage';
import { DocumentRequirementsPage } from '@/features/pages/admin/DocumentRequirementsPage';
import DocumentCompliancePage, { DOCUMENT_COMPLIANCE_PERMS, DOCUMENT_VERIFY_PERMISSION } from '@/features/pages/admin/DocumentCompliancePage';
import { LifecycleQueuePage } from '@/features/pages/admin/LifecycleQueuePage';
import { LifecyclePackagesPage } from '@/features/pages/admin/LifecyclePackagesPage';
import { DeveloperPage } from '@/features/pages/admin/DeveloperPage';
import { OAuthConsentPage } from '@/features/pages/oauth/OAuthConsentPage';
import { AiApprovalPage } from '@/features/pages/ai-approvals/AiApprovalPage';
import { WorkforceOverviewPage } from '@/features/pages/workassignments/WorkforceOverviewPage';
import { WorkAssignmentsPage } from '@/features/pages/workassignments/WorkAssignmentsPage';
import { MyAssignmentsPage } from '@/features/pages/workassignments/MyAssignmentsPage';
import { WorkPage } from '@/features/pages/work/WorkPage';
import { WorkItemPage } from '@/features/pages/work/WorkItemPage';
import WorkAccessPage from '@/features/pages/admin/WorkAccessPage';
import WorkloadPolicyPage from '@/features/pages/admin/WorkloadPolicyPage';
import { DeviceActivateConsentPage } from '@/features/pages/oauth/DeviceActivateConsentPage';
import { WorkforcePage } from '@/features/pages/workforce/WorkforcePage';
import { SetupChecklistPage } from '@/features/onboarding/setup/SetupChecklistPage';
import { SetupBackLink } from '@/features/onboarding/setup/SetupBackLink';
import { PayrollRunsPage, PayrollRunDetailPage, PayrollRegisterPage, LoansPage, FinalSettlementPage, PayrollSettingsPage, ReconciliationPage, FilingsPage, FinanceHubPage } from '@/features/payroll';
import MarketplaceDiscoverPage from '@/features/marketplace/MarketplaceDiscoverPage';
import ProductDetailPage from '@/features/marketplace/ProductDetailPage';
import MyProductsRoute from '@/features/marketplace/MyProductsRoute';
import MyLoansRoute from '@/features/marketplace/MyLoansRoute';
import { AttendancePage } from '@/features/pages/attendance/AttendancePage';
import { BenefitsPage } from '@/features/benefits/BenefitsPage';
import { MyBenefitsPage } from '@/features/benefits/MyBenefitsPage';
import { MyPayPage } from '@/features/my-pay';
import ExpensesPage from '@/features/pages/employee/ExpensesPage';
import ExpenseApprovalsPage from '@/features/pages/admin/ExpenseApprovalsPage';
import ComplianceGate from '@/features/compliance/ComplianceGate';

/** my-work/:id and work-review/:id → work/:id (reviewer mode via ?review=1). */
const LegacyWorkItemRedirect: React.FC<{ review?: boolean }> = ({ review }) => {
  const { itemId } = useParams<{ itemId: string }>();
  return <Navigate to={`../work/${itemId}${review ? '?review=1' : ''}`} replace />;
};

export const TenantLayout: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const currentTenant = useAppSelector((state) => state.tenant?.current);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Desktop notifications: repair a server row this browser has lost.
  //
  // ⚠️ Here, at authenticated app start, rather than on the settings screen.
  // The self-heal was useless where it was: if the subscription row went
  // missing, nothing arrived until the person happened to open
  // Account -> Notifications, which almost nobody does.
  //
  // ⚠️ reconcile() NEVER turns anything on. It needs permission already
  // granted, a live subscription already on this browser, and no explicit
  // opt-out — so it can only restore, never create, and never prompts.
  //
  // Keyed on the user id so switching accounts re-runs it for the new person.
  // Fire-and-forget: a failure must not delay rendering the app.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    void reconcileDesktopNotifications();
  }, [isAuthenticated, user?.id]);

  // The URL should name the workspace, not its id. The saved session only ever kept the
  // tenant's name and id, so fetch the slug once when it's missing…
  useEffect(() => {
    if (!currentTenant?.id || currentTenant.slug || currentTenant.subdomain) return;
    let alive = true;
    getCurrentTenant()
      .then((tn) => {
        if (!alive || !tn?.slug) return;
        dispatch(setCurrentTenant({ ...currentTenant, slug: tn.slug, subdomain: tn.slug, name: tn.name || currentTenant.name }));
      })
      .catch(() => undefined);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id, currentTenant?.slug, currentTenant?.subdomain]);

  // …and then correct an address that is still showing the id, so links people copy from
  // here carry the workspace name. Only a bare id is ever replaced.
  useEffect(() => {
    const nice = shouldHealTenantPath(tenantSlug, currentTenant);
    if (!nice) return;
    const next = swapTenantSegment(location.pathname, nice);
    if (next !== location.pathname) navigate(next + location.search + location.hash, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, currentTenant?.slug, currentTenant?.subdomain, location.pathname]);
  const { userRole, canonicalRoles, permissions: explicitPermissions } = useRoleAccess();
  // Warm the platform feature-flag cache once per authed session, so any
  // useFeatureFlag(...) consumer resolves instantly. (Distinct from the local
  // navigation getFeatureFlags below.)
  usePlatformFeatureFlags();

  // DEMO ONLY: Check for demo role override in development mode
  // In production, the user's actual role from authentication is used
  useEffect(() => {
    // Only apply demo role in development mode
    if (process.env.NODE_ENV === 'development') {
      const demoRole = localStorage.getItem('demo_role');
      if (demoRole && user) {
        // Map demo role to UserRole enum for testing purposes
        let userRoleEnum: any;
        switch (demoRole) {
          case 'system-admin':
            userRoleEnum = 'super_admin';
            break;
          case 'tenant-admin':
            userRoleEnum = 'admin';
            break;
          case 'manager':
            userRoleEnum = 'manager';
            break;
          case 'employee':
          default:
            userRoleEnum = 'employee';
            break;
        }
        
        // Only update if different from current role
        if (user.role !== userRoleEnum) {
          dispatch(updateUser({ role: userRoleEnum }));
        }
      }
    }
    // In production, user.role comes from actual authentication
  }, [user, dispatch]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Map user role to navigation role type
  const getNavigationRole = (): UserRole => {
    if (!userRole) return 'employee';
    
    switch (userRole) {
      case 'system-admin':
        return 'system-admin';
      case 'tenant-admin':
        return 'tenant-admin';
      case 'manager':
        return 'manager';
      case 'employee':
      default:
        return 'employee';
    }
  };

  // Phase 4 (authz-hardening): the nav context now carries the user's REAL
  // effective permission set — canonical keys from the static role map UNIONED
  // with the server-resolved set (dynamic custom roles). The old synthesized
  // 'manage_team'-style vocabulary existed nowhere in the RBAC catalog, so nav
  // gates built on it were fiction.
  const getPermissions = (): Permission[] => {
    const staticPerms = permissionsForRoles(canonicalRoles);
    const explicit = (explicitPermissions as unknown[]).filter(
      (p): p is string => typeof p === 'string',
    );
    return Array.from(new Set([...staticPerms, ...explicit]));
  };

  // Phase 4 (authz-hardening): route-level RBAC gate (any-of canonical
  // permissions; server-resolved set ∪ static role map). Truth-in-UI only —
  // the backend remains the enforcement point. Redirects to dashboard.
  const G = (perms: string[], el: React.ReactNode) => (
    <RequireAnyPermissionRoute perms={perms}>{el}</RequireAnyPermissionRoute>
  );

  // Enabled features come from the BACKEND entitlement system (auth-service
  // tenant_features) — the single authority. localStorage is only a
  // non-authoritative warm-start cache so navigation doesn't flicker while the
  // fetch is in flight; a failed fetch falls back to the cache, never the
  // other way around. Alias codes normalize to their canonical product.
  const FEATURE_ALIASES: Record<string, string> = { expense_management: 'expenses', work_management: 'work' };
  const canonicalFeature = (code: string) => FEATURE_ALIASES[code] || code;
  const featureCacheKey = `amashr_tenant_features_cache_${tenantSlug || 'default'}`;
  const [backendFeatures, setBackendFeatures] = useState<FeatureFlag[] | null>(() => {
    try {
      const cached = localStorage.getItem(featureCacheKey);
      return cached ? (JSON.parse(cached) as FeatureFlag[]) : null;
    } catch { return null; }
  });
  useEffect(() => {
    let active = true;
    import('@/lib/hrApi').then(({ getMyTenantFeatures }) => getMyTenantFeatures()).then((rows) => {
      if (!active) return;
      const enabled = Array.from(new Set(
        (rows || []).filter((r: any) => r.isEnabled).map((r: any) => canonicalFeature(r.featureCode)),
      )) as FeatureFlag[];
      setBackendFeatures(enabled);
      try { localStorage.setItem(featureCacheKey, JSON.stringify(enabled)); } catch { /* cache only */ }
    }).catch(() => { /* keep cache/empty — entitled features fail closed */ });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  const getFeatureFlags = (): FeatureFlag[] => backendFeatures ?? [];

  const navRole = getNavigationRole();
  const isSystemAdmin = navRole === 'system-admin';

  // Check if user should be redirected to admin portal (production behavior)
  // This runs after authentication and checks the actual user role
  useEffect(() => {
    // Only redirect in production or if not in demo mode
    if (process.env.NODE_ENV !== 'development' || !localStorage.getItem('demo_role')) {
      const actualRole = navRole;

      // RBAC Model A: only PLATFORM operators (system-admin) belong in the
      // admin-portal. Company admins (tenant-admin / tenant_owner) manage their
      // organization inside main-app's Administration area, so they are NOT
      // redirected away.
      if (actualRole === 'system-admin') {
        // Hand the platform operator into the admin-portal. The access token now
        // lives in an httpOnly cookie (unreadable by JS), so no token is passed
        // in the URL — the admin-portal establishes its own cookie session.
        adminPortalConfig.redirect(actualRole);
      }
    }
  }, [navRole]);

  // Render appropriate dashboard based on ACTUAL user role
  // This ensures users see the correct dashboard based on their authentication role
  const renderDashboard = () => {
    // Get the actual role from auth or mapped role
    const actualRole = navRole;
    
    // RBAC Model A: only PLATFORM operators (system-admin) are handed to the
    // admin-portal. Company admins (tenant-admin) stay in main-app and fall
    // through to the standard dashboard below.
    if (actualRole === 'system-admin') {
      if (process.env.NODE_ENV === 'development' && localStorage.getItem('demo_role')) {
        // In demo mode, show a placeholder for admin dashboard
        return (
          <div className="flex flex-col items-center justify-center h-full p-8">
            <h1 className="text-h1 text-charcoal mb-4">Admin Dashboard</h1>
            <p className="text-muted-foreground mb-6">
              In production, {actualRole === 'system-admin' ? 'System Admins' : 'Tenant Admins'} 
              {' '}are redirected to the admin portal.
            </p>
            <Button 
              onClick={() => {
                adminPortalConfig.redirect(actualRole);
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Go to Admin Portal
            </Button>
          </div>
        );
      }
      // In production, redirect to admin portal
      adminPortalConfig.redirect(actualRole);
      return null;
    }
    
    // Everyone (employees AND managers) gets the same employee-style dashboard. Managers simply
    // get extra access via the Team Management nav section; there is no separate "Manager Dashboard".
    // (The Home/Today experiment stays reachable at /today; the classic dashboard is the landing.)
    return <EmployeeDashboard />;
  };

  // Global command palette (⌘K). Header search opens it; the palette also has its
  // own keyboard listener.
  const [commandOpen, setCommandOpen] = useState(false);
  const commandUserContext = React.useMemo(
    () => ({
      userId: user?.id || 'default',
      role: navRole,
      permissions: getPermissions(),
      tenantSlug: !isSystemAdmin ? tenantSlug : undefined,
      featureFlags: getFeatureFlags(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, navRole, tenantSlug, isSystemAdmin],
  );

  // Resolve THIS user's actual navigation (registry + management sections,
  // RBAC/entitlement-filtered) once, and hand it to Dana so its "where do I find
  // X" guidance is always current — no hardcoded, drifting screen list.
  const danaNavPages = useResolvedNavPages(commandUserContext);

  // Collapsible sidebar (icons-only when collapsed). Persisted so it stays put.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => typeof window !== 'undefined' && localStorage.getItem('kp-sidebar-collapsed') === '1',
  );
  const toggleSidebarCollapsed = () =>
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('kp-sidebar-collapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });

  return (
    <ComplianceGate>
    <SidebarProvider>
      <MessagesPanelProvider>
      <div className="min-h-screen flex w-full bg-cream">
        <TenantAwareNavigationSidebar
          userId={user?.id || 'default'}
          userRole={navRole}
          tenantSlug={!isSystemAdmin ? tenantSlug : undefined}
          permissions={getPermissions()}
          featureFlags={getFeatureFlags()}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapsed}
        />

        <div className={`relative flex-1 flex flex-col ml-0 min-w-0 transition-[margin] duration-300 print:!ml-0 ${sidebarCollapsed ? 'md:ml-[76px]' : 'md:ml-64'}`}>
          <div className="sticky top-0 z-50 bg-card print:hidden">
            <Header
              userRole={navRole as any}
              tenantSlug={!isSystemAdmin ? tenantSlug : undefined}
              onOpenSearch={() => setCommandOpen(true)}
            />
            <PlatformBanner />
          </div>

          <main className="flex-1 overflow-auto bg-cream print:overflow-visible print:h-auto print:bg-white" style={{ paddingBottom: 'var(--kp-player-h, 0px)' }}>
            <div className="p-4 md:p-8">
              <SetupBackLink />
              <PageBoundary>
              <Routes>
              {/* Dashboard - default route */}
              <Route index element={renderDashboard()} />
              <Route path="dashboard" element={renderDashboard()} />
              
              {/* Employee routes */}
              <Route path="personal" element={<PersonalPage />} />
              <Route path="privacy" element={<ConsentCenterPage />} />
              <Route path="time-off" element={<TimeOffPage />} />
              {/* Conversations (Phase 1: direct messages). Nav is entitlement-gated
                  UX; the gateway + conversation-service enforce the `conversations`
                  entitlement regardless. The page owns /messages/:conversationId. */}
              <Route path="messages" element={<ConversationsPage />} />
              <Route path="messages/:conversationId" element={<ConversationsPage />} />
              <Route path="my-calendar" element={<MyCalendarPage />} />
              {/* Refer & Earn — deliberately unlisted in the sidebar; reached
                  via the avatar dropdown, Dana, or the direct URL. */}
              <Route path="refer" element={<ReferEarnPage />} />
              <Route path="my-benefits" element={<MyBenefitsPage />} />
              <Route path="loans" element={<MyLoansRoute />} />
              <Route path="my-products" element={<MyProductsRoute />} />
              <Route path="documents" element={<EmployeeDocuments />} />
              <Route path="employees" element={<EmployeesPage />} />
              <Route path="employees/:employeeId" element={<EmployeeDetailPage />} />
              {/* My Pay workspace — splat route: the page owns its internal
                  URL-backed tabs (overview/payslips/loans/reports). */}
              <Route path="my-pay/*" element={<MyPayPage />} />
              {/* Expense Management (AMAS pilot): route-level entitlement guard —
                  hidden nav is UX only; direct navigation is still gated here,
                  and the gateway/backend enforce the entitlement regardless. */}
              <Route path="expenses" element={backendFeatures === null
                ? <div className="p-8 text-center text-muted-foreground">…</div>
                : backendFeatures.includes('expenses') ? <ExpensesPage /> : <Navigate to="../dashboard" replace />} />
              <Route path="expense-approvals" element={backendFeatures === null
                ? <div className="p-8 text-center text-muted-foreground">…</div>
                : backendFeatures.includes('expenses') ? G(['expenses:review', 'expenses:approve', 'expenses:finance', 'expenses:read:team', 'expenses:read:all', 'expenses:audit'], <ExpenseApprovalsPage />) : <Navigate to="../dashboard" replace />} />
              {/* Everyday Services (`commerce`): entitlement-gated like Web; the
                  employer switch is enforced by commerce-service (/availability). */}
              <Route path="everyday-services" element={backendFeatures === null
                ? <div className="p-8 text-center text-muted-foreground">…</div>
                : backendFeatures.includes('commerce') ? <EverydayServicesPage /> : <Navigate to="../dashboard" replace />} />
              <Route path="everyday-services/orders/:id" element={backendFeatures === null
                ? <div className="p-8 text-center text-muted-foreground">…</div>
                : backendFeatures.includes('commerce') ? <EverydayServicesPage /> : <Navigate to="../dashboard" replace />} />
              <Route path="performance" element={<PerformancePage />} />
              <Route path="music" element={G(['recognition:read'], <MusicPage />)} />
              <Route path="web" element={backendFeatures === null
                ? <div className="p-8 text-center text-muted-foreground">…</div>
                : backendFeatures.includes('work_browser') ? G(['browser:use'], <WebPage />) : <Navigate to="../dashboard" replace />} />
              <Route path="web/privacy" element={backendFeatures === null
                ? <div className="p-8 text-center text-muted-foreground">…</div>
                : backendFeatures.includes('work_browser') ? G(['browser:use'], <WebPrivacyPage />) : <Navigate to="../dashboard" replace />} />
              <Route path="settings/web" element={backendFeatures === null
                ? <div className="p-8 text-center text-muted-foreground">…</div>
                : backendFeatures.includes('work_browser') ? G(['browser:bookmarks.manage', 'browser:policy.manage', 'tenant:update:own'], <WebSettingsPage />) : <Navigate to="../dashboard" replace />} />
              <Route path="today" element={<HomeToday />} />
              <Route path="hr-actions" element={G(['performance:manage'], <HRActionCenter />)} />
              <Route path="hr-actions/settings" element={G(['performance:manage'], <HRActionSettings />)} />
              <Route path="surveys" element={G(['performance:manage'], <SurveysAdmin />)} />
              <Route path="my-surveys" element={<MySurveys />} />
              <Route path="training" element={<TrainingPage />} />
              {/* Deep link (calendar / notifications / mobile) → Training with the session sheet open. */}
              <Route path="training/sessions/:sessionId" element={<TrainingPage />} />
              <Route path="payslips" element={<Navigate to="../my-pay/payslips" replace />} />
              <Route path="notifications" element={<NotificationsPage />} />

              {/* Payroll (RBAC Model A: company admins run payroll in main-app).
                  Always registered — sidebar visibility is gated by canonical RBAC;
                  the backend enforces payroll permissions on every call. */}
              <Route path="payroll" element={G(['payroll:read'], <PayrollRunsPage />)} />
              <Route path="payroll/runs/:runId" element={G(['payroll:read'], <PayrollRunDetailPage />)} />
              <Route path="payroll/runs/:runId/register" element={G(['payroll:read'], <PayrollRegisterPage />)} />
              <Route path="payroll/salaries" element={G(['payroll:read'], <BulkSalariesPage />)} />
              <Route path="payroll/loans" element={G(['payroll:read'], <LoansPage />)} />
              <Route path="marketplace" element={<MarketplaceDiscoverPage />} />
              <Route path="marketplace/products/:id" element={<ProductDetailPage />} />
              {/* Finance hub (payments overview + reconciliation + filings). The
                  old standalone routes redirect to the matching hub tab. */}
              <Route path="payroll/finance" element={G(['payroll:read'], <FinanceHubPage />)} />
              <Route path="payroll/reconciliation" element={G(['payroll:read'], <ReconciliationPage />)} />
              <Route path="payroll/filings" element={G(['payroll:read'], <FilingsPage />)} />
              <Route path="payroll/final-settlement" element={G(['payroll:read'], <FinalSettlementPage />)} />
              <Route path="payroll/settings" element={G(['payroll:read'], <PayrollSettingsPage />)} />

              {/* Attendance review (scanner imports → exceptions/overtime → payroll).
                  Gated on attendance:read, like every other module route.
                  The previous comment here claimed the page showed its own
                  no-access notice — it does not: AttendancePage has no
                  permission check, so an unauthorized visitor got the full
                  screen and a row of failed calls. The backend always enforced
                  the rule; this makes the refusal legible. */}
              <Route path="attendance" element={G(['attendance:read', 'attendance:connector:read'], <AttendancePage />)} />
              <Route path="workforce-overview" element={<WorkforceOverviewPage />} />
              <Route path="work-assignments" element={<WorkAssignmentsPage />} />
              <Route path="my-assignments" element={<MyAssignmentsPage />} />
              {/* Native Work Management (work-management-service): definition +
                  lifecycle + evidence-gated completion — distinct from the
                  execution/presence pages above. Entitlement-guarded like
                  expenses: hidden nav is UX only; direct navigation is gated
                  here, and the gateway entitlement policy + backend RBAC
                  enforce regardless. */}
              <Route path="work/:itemId" element={<WorkItemPage />} />
              <Route path="work" element={backendFeatures === null
                ? <div className="p-8 text-center text-muted-foreground">…</div>
                : backendFeatures.includes('work') ? <WorkPage /> : <Navigate to="../dashboard" replace />} />
              {/* Legacy aliases (bookmarks, notification links, mobile deep links)
                  → the unified /work tree. Reviewer mode is derived from ?review=1. */}
              <Route path="my-work" element={<Navigate to="../work" replace />} />
              <Route path="my-work/:itemId" element={<LegacyWorkItemRedirect />} />
              <Route path="work-review" element={<Navigate to="../work?tab=reviews" replace />} />
              <Route path="work-review/:itemId" element={<LegacyWorkItemRedirect review />} />
              {/* Same shape as payroll/import above: either the attendance
                  importer's own permission or general migration access. */}
              <Route path="attendance/import" element={G(['attendance:import', 'migration:read'], <MigrationCenterPage defaultTarget="attendance" />)} />

              {/* Benefits module (visibility gated by the tenant benefits settings;
                  the page itself shows a "not enabled" notice when off). */}
              <Route path="benefits" element={<BenefitsPage />} />

              {/* Manager routes */}
              {(navRole === 'manager' || navRole === 'tenant-admin') && (
                <>
                  <Route path="team" element={<TeamManagementPage />} />
                  <Route path="leave-approvals" element={<LeaveApprovalsPage />} />
                  <Route path="team-reports" element={<ManagerReportsPage />} />
                  <Route path="calendar" element={<TeamCalendarPage />} />
                  <Route path="manager/reports" element={<ManagerReportsPage />} />
                </>
              )}
              {/* Someone covering a manager's leave approvals through an ACTIVE
                  delegation (NA-3): the queue only — leave-service lists just
                  the delegated people. No delegation → the dashboard. */}
              {!(navRole === 'manager' || navRole === 'tenant-admin') && (
                <Route path="leave-approvals" element={<DelegatedRoute capability="leave:approve"><LeaveApprovalsPage /></DelegatedRoute>} />
              )}
              
              {/* Company Administration (RBAC Model A) — always registered, since
                  the legacy nav-role path maps company owners to 'manager' and the
                  old `navRole === 'tenant-admin'` gate never matched in production
                  (the routes silently 404'd → bounced to dashboard). Sidebar
                  visibility is gated by canonical RBAC; pages enforce their own. */}
              {/* Standard Report Center (reporting foundation R2): role-aware
                  template registry + generic viewer. Team-leave analytics moved
                  to `team-reports` / `manager/reports` (ManagerReportsPage). */}
              <Route path="reports" element={G(['reports:view'], <ReportsPage />)} />
              {/* Guided Report Builder (R4) — registered BEFORE the :templateId
                  catch-all so `reports/builder` isn't taken as a template id. */}
              <Route path="reports/builder" element={G(['reports:view'], <ReportBuilderPage />)} />
              {/* Saved definitions (R3) must register BEFORE the :templateId
                  catch-all so `reports/saved/<id>` doesn't resolve as a template. */}
              <Route path="reports/saved/:savedId" element={G(['reports:view'], <ReportViewerPage />)} />
              <Route path="reports/:templateId" element={G(['reports:view'], <ReportViewerPage />)} />
              <Route path="settings" element={G(['tenant:update:own'], <SettingsPage />)} />
              <Route path="settings/payments" element={G(['tenant:update:own', 'payroll:read'], <PaymentProviderPage />)} />
              <Route path="payouts" element={G(['tenant:update:own', 'payroll:read'], <PayoutsPage />)} />
              <Route path="commission" element={<CommissionPage />} />
              <Route path="settings/integrations" element={G(['integration:manage', 'email:integration:read', 'tenant:update:own'], <EmailIntegrationsPage />)} />
              <Route path="settings/automation" element={G(['integration:manage', 'expenses:imprest:configure'], <AutomationSettingsPage />)} />
              <Route path="features" element={G(['tenant:update:own'], <FeatureManagementPage />)} />
              <Route path="admin" element={G(['user:read:own-tenant', 'department:write', 'leavepolicy:write', 'tenant:update:own'], <AdministrationPage />)} />
              <Route path="admin/users" element={G(['user:read:own-tenant'], <UsersRolesPage />)} />
              <Route path="admin/roles" element={G(['user:role:assign'], <RolesPermissionsPage />)} />
              <Route path="admin/scope-policy" element={G(['user:role:assign'], <ScopePolicyPage />)} />
              {/* Delegating YOUR OWN responsibilities while you are away is
                  self-service, not administration — so this route carries no
                  permission guard. It was guarded by approver permissions, and
                  because the guard redirects to the dashboard, "Set up
                  delegation" on the leave form was a dead end for everyone who
                  is not an approver: the one in-flow path to satisfy a policy
                  that REQUIRES delegation.
                  Nothing is exposed by opening it. The page reads
                  GET /delegations, which the backend scopes to the caller
                  (listMine), creates with delegator = caller and only over
                  capabilities the caller holds, and its one admin section (the
                  approval queue) is already gated on user:role:assign inside
                  the page. The nav entry stays permission-gated, so this does
                  not appear in an ordinary employee's admin menu. */}
              <Route path="admin/delegations" element={<DelegationsPage />} />
              <Route path="admin/delegation-policy" element={G(['user:role:assign'], <DelegationPolicyPage />)} />
              <Route path="admin/work-access" element={G(['work:manage'], <WorkAccessPage />)} />
              <Route path="admin/workload" element={G(['work:manage'], <WorkloadPolicyPage />)} />
              <Route path="admin/departments" element={G(['department:write'], <DepartmentsPage />)} />
              <Route path="admin/job-titles" element={G(['department:write'], <JobTitlesPage />)} />
              <Route path="admin/compliance" element={G(['user:read:own-tenant'], <CompliancePage />)} />
              <Route path="admin/audit" element={G(['audit:view'], <AuditCenterPage />)} />
              <Route path="admin/billing" element={G(['tenant:update:own', 'billing:read:own'], <BillingPage />)} />
              <Route path="admin/leave-policies" element={G(['leavepolicy:write'], <LeavePoliciesPage />)} />
              <Route path="admin/holidays" element={G(['holiday:manage'], <HolidaysPage />)} />
              <Route path="admin/import" element={G(['employee:import'], <BulkImportPage />)} />
              <Route path="admin/migration" element={G(['migration:read'], <MigrationCenterPage />)} />
              <Route path="payroll/import" element={G(['payroll:read', 'migration:read'], <MigrationCenterPage defaultTarget="payroll" />)} />
              <Route path="admin/ai-assistant" element={G(['ai-config:write'], <AiAssistantPage />)} />
              <Route path="admin/change-approvals" element={G(['employee:change:approve', 'leave:approve'], <ChangeApprovalsPage />)} />
              <Route path="admin/payment-approvals" element={G(['employee:payment:approve'], <PaymentMethodApprovalsPage />)} />
              <Route path="admin/document-requirements" element={G(['user:read:own-tenant', 'department:write', 'leavepolicy:write', 'tenant:update:own'], <DocumentRequirementsPage />)} />
              <Route path="admin/document-compliance" element={<RequireAnyPermissionRoute perms={DOCUMENT_COMPLIANCE_PERMS} orTenantWide={DOCUMENT_VERIFY_PERMISSION}><DocumentCompliancePage /></RequireAnyPermissionRoute>} />
              <Route path="admin/lifecycle" element={G(['leave:approve', 'employee:write'], <LifecycleQueuePage />)} />
              <Route path="admin/lifecycle-packages" element={G(['tenant:update:own'], <LifecyclePackagesPage />)} />
              <Route path="admin/developer" element={G(['tenant:update:own'], <DeveloperPage />)} />
              <Route path="oauth/consent" element={<OAuthConsentPage />} />
              <Route path="ai-approvals/:id" element={<AiApprovalPage />} />
              <Route path="oauth/activate" element={<DeviceActivateConsentPage />} />
              <Route path="admin/workforce" element={G(['workforce:framework:read'], <WorkforcePage />)} />
              <Route path="admin/learning" element={G(['learning:manage'], <LearningAdminPage />)} />
              {/* Team lane: permission-gated (not nav-role-gated) so ANY people
                  manager — custom roles included — reaches it. */}
              <Route path="team-learning" element={G(['learning:assign'], <TeamLearningPage />)} />
              <Route path="admin/setup" element={G(['department:write'], <SetupChecklistPage />)} />
              
              {/* System admin routes */}
              {navRole === 'system-admin' && (
                <>
                  <Route path="tenants" element={
                    <div className="text-center py-8">
                      <h2 className="text-h2 text-charcoal mb-4">Tenants Management</h2>
                      <p className="text-muted-foreground">Manage all tenants in the system</p>
                    </div>
                  } />
                  <Route path="monitoring" element={
                    <div className="text-center py-8">
                      <h2 className="text-h2 text-charcoal mb-4">System Monitoring</h2>
                      <p className="text-muted-foreground">Monitor system health and performance</p>
                    </div>
                  } />
                  <Route path="security" element={
                    <div className="text-center py-8">
                      <h2 className="text-h2 text-charcoal mb-4">Security Settings</h2>
                      <p className="text-muted-foreground">Manage security configurations</p>
                    </div>
                  } />
                  <Route path="system" element={<SettingsPage />} />
                </>
              )}
              
              {/* Common routes */}
              <Route path="account" element={<AccountSettingsPage />} />
              <Route path="help" element={<HelpPage />} />
              
              {/* Catch all - redirect to dashboard */}
              <Route path="*" element={<Navigate to="" replace />} />
            </Routes>
              </PageBoundary>
          </div>
        </main>
        {/* Docked music bar — a flex sibling of <main>, so it reserves its own height and page
            content (incl. the Conversations composer) sits above it, never behind it. */}
        <PersistentPlayer />
      </div>
      <AssistantPanel navPages={danaNavPages} />
      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} userContext={commandUserContext} />
      {/* The in-app half of the offline story. The offline PAGE covers a failed
          navigation; this covers requests failing underneath somebody who is
          still looking at a working-looking screen. Renders nothing unless
          something is actually wrong. */}
      <ConnectionBanner />
    </div>
      </MessagesPanelProvider>
    </SidebarProvider>
    </ComplianceGate>
  );
};